/**
 * On-demand variant availability lookup. When a caller passes a bare variant
 * id (no availability fields), the SDK fetches `availableForSale` /
 * `currentlyNotInStock` itself from the shop's Storefront API.
 *
 * Requests are BATCHED: every id requested within the same microtask becomes
 * one `nodes(ids: [...])` query (so a collection page asking about 40
 * variants makes one request), and results are cached in memory for a short
 * TTL — availability is volatile, so it is deliberately not persisted in
 * sessionStorage like the merchant configuration is.
 *
 * When the SDK is configured with a `country`, the query is wrapped in
 * `@inContext(country: ...)` so availability is market-correct. Without it,
 * Shopify answers for the shop's default context — pass your own variant
 * object (from an `@inContext` product query) if you localize inventory.
 */
import { storefrontGraphql, type StorefrontQueryContext } from './metafields'
import { warn } from './util'
import type { VariantAvailability } from './variant-input'

/** Availability changes with every sale — keep the cache short-lived. */
export const AVAILABILITY_TTL_MS = 60 * 1000

/** Storefront API `nodes()` accepts at most 250 ids per call. */
const MAX_IDS_PER_QUERY = 250

function buildQuery(country: string | undefined): string {
  const context = country ? ` @inContext(country: ${country.toUpperCase()})` : ''
  return `query StoqVariantAvailability($ids: [ID!]!)${context} {
  nodes(ids: $ids) {
    ... on ProductVariant { id availableForSale currentlyNotInStock }
  }
}`
}

interface VariantNode {
  id?: string
  availableForSale?: boolean
  currentlyNotInStock?: boolean
}

interface CacheEntry {
  value: VariantAvailability | null
  expiresAt: number
}

export class AvailabilityLookup {
  private readonly ctx: Pick<StorefrontQueryContext, 'shop' | 'token' | 'apiVersion'>
  private readonly country: string | undefined
  private readonly cache = new Map<number, CacheEntry>()
  private queue = new Map<number, Array<(value: VariantAvailability | null) => void>>()
  private flushScheduled = false

  constructor(
    ctx: Pick<StorefrontQueryContext, 'shop' | 'token' | 'apiVersion'>,
    country?: string,
  ) {
    this.ctx = ctx
    this.country = country
  }

  /** Drops all cached entries (used by `client.refresh()`). */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Resolves the variant's availability, batched with every other lookup in
   * the same microtask. Resolves `null` (unknown) when the variant does not
   * exist, is not exposed to the Storefront API, or the query fails — the
   * caller degrades exactly like missing availability input.
   */
  get(variantId: number): Promise<VariantAvailability | null> {
    const cached = this.cache.get(variantId)
    if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.value)

    return new Promise((resolve) => {
      const waiters = this.queue.get(variantId)
      if (waiters) {
        waiters.push(resolve)
        return
      }
      this.queue.set(variantId, [resolve])
      if (!this.flushScheduled) {
        this.flushScheduled = true
        queueMicrotask(() => {
          this.flushScheduled = false
          void this.flush()
        })
      }
    })
  }

  private async flush(): Promise<void> {
    const batch = this.queue
    this.queue = new Map()
    const ids = [...batch.keys()]

    const results = new Map<number, VariantAvailability | null>()
    let failed = false
    try {
      for (let i = 0; i < ids.length; i += MAX_IDS_PER_QUERY) {
        const chunk = ids.slice(i, i + MAX_IDS_PER_QUERY)
        const data = await storefrontGraphql<{ nodes?: Array<VariantNode | null> | null }>(
          this.ctx,
          buildQuery(this.country),
          { ids: chunk.map((id) => `gid://shopify/ProductVariant/${id}`) },
          'variant availability',
        )
        for (const node of data.nodes ?? []) {
          const id = node?.id ? Number(node.id.split('/').pop()) : NaN
          if (!Number.isNaN(id) && node && node.availableForSale !== undefined) {
            results.set(id, {
              availableForSale: node.availableForSale,
              currentlyNotInStock: node.currentlyNotInStock,
            })
          }
        }
      }
    } catch (error) {
      failed = true
      warn('variant availability lookup failed; treating availability as unknown', error)
    }

    const expiresAt = Date.now() + AVAILABILITY_TTL_MS
    for (const [id, waiters] of batch) {
      const value = results.get(id) ?? null
      // A *successful* lookup that returned no node (variant not exposed to
      // the Storefront API) is a real answer and is cached like any other.
      // A failed request (5xx, network, GraphQL errors) is not — caching its
      // `null` would pin "not preorderable" for the whole TTL after a blip;
      // leave the entry empty so the next call simply retries.
      if (!failed) this.cache.set(id, { value, expiresAt })
      for (const resolve of waiters) resolve(value)
    }
  }
}
